import * as React from "react"
import { Link, router, usePage } from "@inertiajs/react"
import { Select } from "@/components/ui/select"
import { cn } from "@/lib/utils"

export type SubNavItem = {
  href: string
  label: string
  /**
   * Whether this tab is the current one. Omit it and the current URL is matched
   * against `href` — exactly, or as a path prefix, so a drill-down keeps its
   * parent tab lit. Pass a boolean to drive the state by hand (the design-system
   * preview does this so its tabs do not navigate anywhere).
   */
  active?: boolean | ((url: string) => boolean)
}

/**
 * The sub-navigation tab strip documented at /admin/design-system. Sits inside a
 * page header's `tabs` slot, so the header's hairline and the strip's own read as
 * two lines. Below `md` the tabs collapse into a full-width dropdown rather than
 * scrolling sideways or wrapping.
 *
 * `actions` is the optional right-hand slot — secondary links, filters, or a
 * picker. On mobile it stacks above the dropdown so the dropdown stays truly
 * full-width.
 */
export function SubNav({
  items,
  actions,
  ariaLabel = "Section",
}: {
  items: SubNavItem[]
  actions?: React.ReactNode
  ariaLabel?: string
}) {
  const { url } = usePage()

  const isActive = (item: SubNavItem) => {
    if (typeof item.active === "boolean") return item.active
    if (typeof item.active === "function") return item.active(url)
    return url === item.href || url.startsWith(`${item.href}/`)
  }

  const current = items.find(isActive) ?? items[0]

  return (
    <>
      {/* Mobile (< md): the right-hand slot stacks above a full-width dropdown. */}
      <div className="border-b border-hairline md:hidden">
        <div className="flex flex-col gap-2 py-2">
          {actions && <div className="flex items-center gap-3">{actions}</div>}
          <Select
            aria-label={ariaLabel}
            value={current?.href ?? ""}
            onChange={(event) => router.visit(event.target.value)}
            className="w-full"
          >
            {items.map((item) => (
              <option key={item.href} value={item.href}>
                {item.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Desktop (md+): horizontal tab strip. */}
      <nav
        aria-label={ariaLabel}
        className="hidden items-end justify-between gap-4 border-b border-hairline md:flex"
      >
        <div className="flex items-center gap-6">
          {items.map((item) => {
            const active = isActive(item)
            return (
              <Link
                key={item.href}
                href={item.href}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "-mb-px cursor-pointer border-b-2 px-1 py-3 text-sm no-underline",
                  active
                    ? "border-accent font-medium text-accent-display"
                    : "border-transparent text-ink-body hover:text-ink-display",
                )}
              >
                {item.label}
              </Link>
            )
          })}
        </div>

        {/*
          `self-stretch` opts the slot out of the nav's `items-end`, so it fills
          the strip's full height and centres its contents between the two
          hairlines. A control as tall as the row is the common case; centring
          also lands text-only actions on the tab labels, which sit centred in
          their own symmetric `py-3` box.
        */}
        {actions && (
          <div className="flex items-center gap-3 self-stretch">{actions}</div>
        )}
      </nav>
    </>
  )
}
